import { ImagePlaceholder, style, UserIcon, UserPlusIcon } from "@r360/library";
import classNames from "classnames";
import React, { useEffect } from "react";
import { getGuestAge } from "../../Helper";
import useTranslate from "../../hooks/useTranslate";
import { TGuest } from "../../store/types";

type GuestListProps = {
    guests: [string, TGuest][];
    guestToEdit: string | null;
    newGuestIsOpen: boolean;
    cartIsEmpty: boolean;
    toggleGuestToEdit: (guestId: string) => void;
    toggleNewGuestIsOpen: () => void;
};

export const GuestList: React.FC<GuestListProps> = ({
    guests,
    guestToEdit,
    newGuestIsOpen,
    cartIsEmpty,
    toggleGuestToEdit,
    toggleNewGuestIsOpen,
}) => {
    const t = useTranslate();

    useEffect(() => {
        if (guestToEdit) {
            focusOnGuestForm(guestToEdit);
        }
    }, [guestToEdit]);

    const getImageUrlFromHash = (imageHash: string) => {
        if (!imageHash) {
            return "";
        }

        const baseImageUrl = "https://r360generalstorage.blob.core.windows.net/userprofileimages/";
        const imageExtension = ".jpg";

        return `${baseImageUrl}${imageHash}${imageExtension}`;
    };

    const focusOnGuestForm = (guestId: string) => {
        setTimeout(() => {
            const guestForm = document.getElementById(`edit-guest-form-${guestId}`);
            if (guestForm) {
                console.log("focusing on guest form", guestForm);
                guestForm.focus();
            }
        }, 500);
    };

    return (
        <ul
            className={classNames("my-guests__guests-list", {
                "u-disabled": !cartIsEmpty,
            })}
        >
            {guests.map(([guestId, guest]) => {
                const guestIsSelected = guestToEdit === guestId;
                const guestHasImage = guest.imagehash;

                return (
                    <li
                        key={guestId}
                        className="my-guests__guests-list-item"
                        onClick={() => toggleGuestToEdit(guestId)}
                        onKeyDown={e => e.key === "Enter" && toggleGuestToEdit(guestId)}
                        tabIndex={0}
                        role="button"
                        aria-label={guest.firstname}
                        aria-controls={`edit-guest-form-${guestId}`}
                        aria-expanded={guestIsSelected}
                        id={`guest-${guestId}`}
                    >
                        <div
                            className={classNames("my-guests__guests-list-img", {
                                "my-guests__guests-list-img--selected": guestIsSelected,
                            })}
                            style={{
                                backgroundImage:
                                    guestHasImage && guest.imagehash
                                        ? `url(${getImageUrlFromHash(guest.imagehash)})`
                                        : "none",
                            }}
                            data-private
                        >
                            {!guestHasImage && (
                                <ImagePlaceholder
                                    fillParent
                                    customIcon={<UserIcon color={style.whiteColor} size={42} />}
                                />
                            )}
                        </div>
                        <div className="my-guests__guests-list-text">
                            <span>
                                {guest.firstname} {`(${getGuestAge(guest, t("book.general.years"), "")})`}
                            </span>
                        </div>
                    </li>
                );
            })}
            <li className="my-guests__guests-list-item" onClick={() => toggleNewGuestIsOpen()}>
                <div
                    className={classNames("my-guests__guests-list-img my-guests__guests-list-img--new-guest", {
                        "my-guests__guests-list-img--selected": newGuestIsOpen,
                    })}
                >
                    <div className="my-guests__new-guest-icon">
                        <UserPlusIcon size={56} color={style.whiteColor} />
                    </div>
                </div>
                <div className="my-guests__guests-list-text">
                    <span>{t("book.checkout.add_guest")}</span>
                </div>
            </li>
        </ul>
    );
};
